/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import { Formik, Form, FormikProps } from 'formik';
import { LargeInputContainer, Label } from '@espacePro/components/_common/Form';
import { CustomReactSelect } from '@shared/fields/CustomReactSelect';
import { OptionWithSubtitle } from '@shared/OptionWithSubtitle';
import { SchoolType } from '@espacePro/components/CMS/types/SchoolType';
import { Link } from '@espacePro/components/_common/Link';
import { FONT_FAMILY_MAIN } from '@espacePro/constants/admin-theme';
import stores from '../../../../../main-app/stores';
import Field from '../../../../../main-app/components/fields/Field';
import TextareaField from '../../../../../main-app/components/fields/Textareafield';
import Btn from '../../../../../main-app/components/buttons/Btn';
import { FormColor, FormValues, Section } from '../types/AppointmentFormTypes';

type AppointmentFormProps = {
  sections: Section[];
  formColors: FormColor[];
  schools?: SchoolType[];
  schoolId: string;
  meetingSubjects: any[];
  isSubjectsLoading: boolean;
  isSchoolsLoading: boolean;
};

type FormStyle = {
  backgroundColor: string;
  fieldsColor: string;
  fieldsTextColor: string;
  labelsColor: string;
  buttonColor: string;
  buttonTextColor: string;
};

const StyledField = styled(Field)`
  font-family: ${FONT_FAMILY_MAIN};
`;

const StyledButton = styled(Btn)<{
  marginTop?: number;
}>`
  margin-top: ${({ marginTop }) => marginTop}px;
`;

const StyledSelect = styled(CustomReactSelect)`
  font-family: ${FONT_FAMILY_MAIN};
`;

const StyledInputContainer = styled(LargeInputContainer)`
  max-width: none;
`;

const StyledForm = styled(Form)<{
  backgroundColor: string;
}>`
  background-color: ${({ backgroundColor }) => backgroundColor};
  padding: 20px;
  border-radius: 10px;
`;

const StyledLabel = styled(Label)<{ color: string }>`
  margin: 0;
  color: ${({ color }) => color};
  font-family: ${FONT_FAMILY_MAIN};
`;

const DescriptionText = styled.p`
  font-size: 10px;
  color: #fff;
  font-family: ${FONT_FAMILY_MAIN};
  color: ${({ textColor }) => textColor};
`;

const FormSchema = Yup.object().shape({
  firstname: Yup.string().required('Ce champ est requis'),
  lastname: Yup.string().required('Ce champ est requis'),
  email: Yup.string().email('Adresse email invalide').required('Ce champ est requis'),
  phone: Yup.string()
    .matches(
      /^(?:(?:)0)\s*[1-9](?:[\s.-]*\d{2}){4}$/,
      'Le téléphone doit être au format 01 02 03 04 05',
    )
    .required('Ce champ est obligatoire.'),
});

const AppointmentForm = ({
  sections,
  formColors,
  schools = [],
  schoolId,
  meetingSubjects,
  isSubjectsLoading = false,
  isSchoolsLoading = false,
}: AppointmentFormProps) => {
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const {
    backgroundColor: formBackgroundColor,
    fieldsColor,
    fieldsTextColor,
    labelsColor,
    buttonColor,
    buttonTextColor,
  }: FormStyle = formColors.reduce<FormStyle>(
    (acc, color) => {
      acc[color.area as keyof FormStyle] = color.color;
      return acc;
    },
    {
      backgroundColor: '',
      fieldsColor: '',
      labelsColor: '',
      fieldsTextColor: '',
      buttonColor: '',
      buttonTextColor: '',
    },
  );

  const options = schools.map((school: SchoolType) => ({
    value: school.id,
    label: (
      <OptionWithSubtitle
        title={school.title}
        subtitle={`${school.street} ${school.zipcode} ${school.city}`}
      />
    ),
  }));

  const subjects = meetingSubjects.map((subject) => ({
    value: subject.name,
    label: subject.name,
  }));

  const onFormSubmit = async (data: FormValues, formikBag) => {
    setIsFormSubmitting(true);
    const formattedData = {
      school_id: data.school || schoolId,
      first_name: data.firstname,
      last_name: data.lastname,
      email: data.email,
      phone: data.phone,
      category: data.formation,
      message: data.message,
    };

    try {
      await stores.meetingStore.bookMeeting(formattedData, formikBag);
      formikBag.resetForm();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
    } finally {
      setIsFormSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        formation: '',
        school: '',
        message: '',
      }}
      validationSchema={FormSchema}
      onSubmit={onFormSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setTouched,
        setFieldValue,
      }: FormikProps<FormValues>) => (
        <StyledForm backgroundColor={formBackgroundColor}>
          {sections.map((section) => {
            if (!section.isChecked) return null;

            if (section.area === 'formation') {
              return (
                <StyledInputContainer>
                  <StyledLabel color={labelsColor} htmlFor={section.id}>
                    {section.name}
                  </StyledLabel>
                  <StyledSelect
                    id={section.id}
                    isLoading={isSubjectsLoading}
                    inputBackgroundColor={fieldsColor}
                    name={section.area}
                    placeholder={section.placeholder}
                    onChange={(activeOption) => setFieldValue('formation', activeOption.value)}
                    options={subjects}
                    value={subjects.filter((option) => values.formation?.includes(option.value))}
                    onBlur={() => setTouched({ school: true })}
                    hasError={touched.school && !!errors.school}
                  />
                  <div style={{ height: 20 }} />
                </StyledInputContainer>
              );
            }

            if (section.area === 'agency') {
              return (
                <StyledInputContainer>
                  <StyledLabel color={labelsColor} htmlFor={section.id}>
                    Où souhaitez vous faire votre formation?
                  </StyledLabel>
                  <StyledSelect
                    isLoading={isSchoolsLoading}
                    id={section.id}
                    name={section.area}
                    inputBackgroundColor={fieldsColor}
                    placeholder={section.placeholder}
                    onChange={(activeOption) => setFieldValue('school', activeOption.value)}
                    options={options}
                    value={options.filter((option) => values.school?.includes(option.value))}
                    onBlur={() => setTouched({ school: true })}
                    hasError={touched.school && !!errors.school}
                  />
                  <div style={{ height: 20 }} />
                </StyledInputContainer>
              );
            }

            if (section.area === 'message') {
              return (
                <div
                  style={{
                    marginBottom: 10,
                  }}
                >
                  <TextareaField
                    type="text"
                    label={section.name}
                    placeholder={section.placeholder}
                    name={section.area}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values[section.area]}
                    valid={touched[section.area] && !errors[section.area]}
                    errorMessage={
                      errors[section.area] && touched[section.area] ? errors[section.area] : null
                    }
                    backgroundColor={fieldsColor}
                    inputTextColor={fieldsTextColor}
                    labelTextColor={labelsColor}
                    rows={2}
                    resize="vertical"
                  />
                </div>
              );
            }

            return (
              <StyledField
                type="text"
                label={section.name}
                placeholder={section.placeholder}
                name={section.area}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values[section.area]}
                valid={touched[section.area] && !errors[section.area]}
                errorMessage={
                  errors[section.area] && touched[section.area] ? errors[section.area] : null
                }
                backgroundColor={fieldsColor}
                inputTextColor={fieldsTextColor}
                labelTextColor={labelsColor}
              />
            );
          })}
          <DescriptionText textColor={labelsColor}>
            Votre demande et vos coordonnées seront transmises à l’auto-école. Pour plus
            d’informations, consultez notre
            {' '}
            <Link href="/a-propos/donnees-personnelles" target="_blank" rel="noopener noreferrer">
              Politique de protection des données personnelles
            </Link>
          </DescriptionText>
          <StyledButton
            type="submit"
            text="Envoyer"
            block
            disabled={
              errors.firstname
              || errors.lastname
              || errors.email
              || errors.phone
              || errors.formation
              || errors.school
            }
            isLoading={isFormSubmitting}
            color={buttonColor}
            textColor={buttonTextColor}
          />
        </StyledForm>
      )}
    </Formik>
  );
};

export default AppointmentForm;
